import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { mergeForceFamilyData } from './force-calibration-profile';
import {
  certificateImageAsDataUrl,
  drawCertificateFooter,
  drawCertificateHeader,
  drawCertificateLegalText,
  drawReferenceStandardsTable,
} from './certificate-pdf-layout';

const LOGO_URL = '/logo-calins-color-hd.png';
const SIGNATURE_URL = new URL('../assets/firma-daniel-kemeny.png', import.meta.url).href;

function numberOrNull(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function fmt(value, decimals = 3) {
  const number = numberOrNull(value);
  return number === null ? '—' : number.toFixed(decimals).replace('.', ',');
}

function fmtPct(value, decimals = 2) {
  const number = numberOrNull(value);
  return number === null ? '—' : `${number.toFixed(decimals).replace('.', ',')} %`;
}

function dmy(value) {
  if (!value) return '—';
  const text = String(value);
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(text) ? `${text}T12:00:00` : text);
  if (Number.isNaN(date.getTime())) return '—';
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
}

function certificateNumber(...values) {
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (/^\d+$/.test(text)) return text;
    const match = text.match(/OS-(\d+)-(\d+)$/i);
    if (match) return `${match[1]}${match[2]}`;
  }
  return '—';
}

async function imageAsDataUrl(url) {
  return certificateImageAsDataUrl(url);
}

function header(doc, { logo, number, page, pages, first = false }) {
  drawCertificateHeader(doc, { logo, number, page, pages, first });
}

function footer(doc, page, pages) {
  drawCertificateFooter(doc, page, pages);
}

function legalText(doc, y) {
  return drawCertificateLegalText(doc, y);
}

function labelValue(doc, label, value, x, y, valueX = x + 42) {
  doc.setFont('helvetica', 'bold');
  doc.text(label, x, y);
  doc.setFont('helvetica', 'normal');
  doc.text(String(value ?? '—'), valueX, y);
}

function drawPageOne(doc, { session, instrument, familyData }) {
  let y = legalText(doc, 66) + 3;
  const unit = session.instrument_unit || familyData.values_normalized_to || 'Nm';
  const determination = familyData.subfamily === 'torque' ? 'Calibración de torque' : 'Calibración de fuerza';
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.text('ESPECIFICACIONES', 14, y); y += 8;
  doc.setFontSize(9.5);
  const left = [
    ['Objeto:', session.instrument_object || instrument?.type || determination],
    ['Fabricante:', session.instrument_brand || instrument?.brand || '—'],
    ['Modelo:', session.instrument_model || instrument?.model || '—'],
    ['N.° de serie:', session.instrument_serial || instrument?.serial || '—'],
    ['TAG:', session.instrument_tag || instrument?.tag || '—'],
    ['Intervalo:', `${fmt(session.instrument_range_min, 2)} a ${fmt(session.instrument_range_max, 2)} ${unit}`],
  ];
  const right = [
    ['Determinación:', determination],
    ['Resolución:', `${fmt(session.instrument_resolution, 4)} ${unit}`],
    ['Clase:', familyData.tool_class || '—'],
    ['Fecha calibración:', dmy(session.calibration_date)],
    ['Fecha emisión:', dmy(session.report_issue_date)],
    ['Válido hasta:', dmy(session.valid_until)],
  ];
  left.forEach(([label, value], index) => labelValue(doc, label, value, 14, y + (index * 5.5), 48));
  right.forEach(([label, value], index) => labelValue(doc, label, value, 110, y + (index * 5.5), 150));
  y += 43;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.text('CLIENTE', 14, y); y += 8;
  doc.setFontSize(9.5);
  [['Razón social:', session.client_name], ['Dirección:', session.client_address], ['Ciudad:', session.client_city], ['Provincia:', session.client_province]]
    .forEach(([label, value], index) => labelValue(doc, label, value || '—', 26, y + (index * 5.5), 58));
  y += 31;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.text('CONDICIONES AMBIENTALES', 14, y);
  autoTable(doc, {
    startY: y + 7,
    margin: { left: 28, right: 28 },
    theme: 'grid',
    styles: { fontSize: 9, halign: 'center', cellPadding: 2, lineWidth: 0.1 },
    headStyles: { fillColor: [180, 215, 225], textColor: [0, 0, 0], fontStyle: 'bold' },
    head: [['Temperatura ambiente', 'Presión atmosférica', 'Humedad relativa']],
    body: [[`${fmt(session.ambient_temp_avg, 1)} °C`, `${fmt(session.ambient_pressure_avg, 1)} hPa`, `${fmt(session.ambient_humidity_avg, 1)} %HR`]],
  });
}

function drawPageTwo(doc, { session, results, familyData }) {
  const unit = session.instrument_unit || familyData.values_normalized_to || 'Nm';
  const details = Array.isArray(familyData.result_details) ? familyData.result_details : [];
  const detailMap = new Map(details.map((detail) => [`${detail.block_id}:${detail.point_no}`, detail]));
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12);
  doc.text('RESULTADOS OBTENIDOS DURANTE LA CALIBRACIÓN', 14, 40);
  const sorted = [...results].sort((a, b) => String(a.block_id).localeCompare(String(b.block_id)) || a.point_no - b.point_no);
  autoTable(doc, {
    startY: 48,
    margin: { left: 10, right: 10 },
    theme: 'grid',
    styles: { fontSize: 7.6, halign: 'center', cellPadding: 1.7, lineWidth: 0.1 },
    headStyles: { fillColor: [180, 215, 225], textColor: [0, 0, 0], fontStyle: 'bold' },
    head: [['Sentido', 'Pto.', `Media patrón\n${unit}`, `Media instrumento\n${unit}`, `Error\n${unit}`, 'Error %', 'Repetibilidad', `U (k=2)\n${unit}`, 'Tolerancia', 'Conforme']],
    body: sorted.map((result) => {
      const detail = detailMap.get(`${result.block_id}:${result.point_no}`) || {};
      return [
        detail.direction === 'CW' ? 'Horario' : detail.direction === 'CCW' ? 'Antihorario' : 'Carga',
        result.point_no,
        fmt(result.pattern_mean),
        fmt(result.instrument_mean),
        fmt(result.error_abs),
        fmtPct(result.error_pct),
        fmt(result.repeatability),
        fmt(result.u_reported),
        `± ${fmt(detail.tolerance_abs)}`,
        result.in_tolerance == null ? '—' : result.in_tolerance ? 'Sí' : 'No',
      ];
    }),
  });
  let y = (doc.lastAutoTable?.finalY || 110) + 12;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9.5);
  const notes = [
    'Error del instrumento = indicación del instrumento − valor del patrón.',
    'La incertidumbre expandida se informa con factor de cobertura k=2, para un nivel de confianza cercano al 95 %.',
    familyData.decision_rule === 'guard_band'
      ? 'La conformidad se evalúa mediante banda de guarda: |error| + U ≤ tolerancia.'
      : 'La conformidad se evalúa comparando el error con la tolerancia declarada.',
  ];
  notes.forEach((note) => {
    const lines = doc.splitTextToSize(note, 180);
    doc.text(lines, 14, y);
    y += (lines.length * 4.6) + 3;
  });
}

function drawPageThree(doc, { session, familyData, signature }) {
  const reference = familyData.standard_configuration?.reference || {};
  const asset = reference.asset || {};
  const capability = reference.capability || {};
  const certificate = reference.certificate || {};
  const budget = familyData.standard_budget || {};
  let y = 42;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text('MÉTODO Y PROCEDIMIENTO', 14, y); y += 8;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9.5);
  doc.text(doc.splitTextToSize(familyData.method, 180), 14, y); y += 7;
  doc.text(doc.splitTextToSize(familyData.procedure, 180), 14, y); y += 14;
  drawReferenceStandardsTable(doc, {
    startY: y,
    references: [{ ...reference, usage: 'Referencia' }],
  });
  y = (doc.lastAutoTable?.finalY || y) + 11;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text('PRESUPUESTO DE INCERTIDUMBRE', 14, y);
  autoTable(doc, {
    startY: y + 5,
    margin: { left: 14, right: 14 },
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 1.8, lineWidth: 0.1 },
    headStyles: { fillColor: [180, 215, 225], textColor: [0, 0, 0], fontStyle: 'bold' },
    head: [['Contribución', 'Tratamiento', 'Dato de entrada']],
    body: [
      ['Calibración del patrón', 'U/k', `Certificado ${certificate.certificate_no || '—'} · k=${fmt(certificate.coverage_factor, 0)}`],
      ['Resolución del instrumento', 'd/√12', `${fmt(session.instrument_resolution, 4)} ${session.instrument_unit || '—'}`],
      ['Repetibilidad', 's/√n', `${familyData.repeat_count || 5} repeticiones por punto`],
      ['Deriva del patrón', 'Límite/√3', fmtPct(budget.drift_percent)],
      ['Influencia de temperatura', 'Límite/√3', `${fmt(budget.temperature_coefficient_percent_per_10c, 3)} % / 10 °C`],
      ['Alineación y otras', 'RSS', `${fmtPct(budget.alignment_uncertainty_percent)} · ${fmtPct(budget.other_uncertainty_percent)}`],
    ],
  });
  y = (doc.lastAutoTable?.finalY || y) + 12;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.text('OBSERVACIONES', 14, y); y += 8;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9.5);
  const observations = session.notes || 'Sin observaciones.';
  doc.text(doc.splitTextToSize(observations, 180), 14, y);
  if (signature) doc.addImage(signature, 'PNG', 78, 238, 44, 29, undefined, 'FAST');
}

export async function generateForceNoSacCertificatePdf(
  { session, instrument, results = [] },
  options = {},
) {
  if (!session) throw new Error('La sesión es requerida.');
  if (!results.length) throw new Error('No hay resultados para certificar.');
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const [logo, signature] = await Promise.all([
    imageAsDataUrl(options.logoUrl || LOGO_URL),
    imageAsDataUrl(options.signatureUrl || SIGNATURE_URL),
  ]);
  const number = certificateNumber(options.serviceOrderCode, session.service_order_code, session.certificate_number, instrument?.lab_id);
  const familyData = mergeForceFamilyData(session.family_data);
  drawPageOne(doc, { session, instrument, familyData });
  doc.addPage(); drawPageTwo(doc, { session, results, familyData });
  doc.addPage(); drawPageThree(doc, { session, familyData, signature });
  const pages = doc.getNumberOfPages();
  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page);
    header(doc, { logo, number, page, pages, first: page === 1 });
    footer(doc, page, pages);
  }
  if (options.returnBlob) return new Blob([doc.output('arraybuffer')], { type: 'application/pdf' });
  doc.save(options.filename || `${number !== '—' ? number : 'R-DT-PC-FORCE'}.pdf`);
  return null;
}

